import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import {simpleFetch, currencyFormat, yearList} from "src/utils/helpsers";
import { NULL_DISPLAY_TEXT } from '../../../components/atoms/NullDisplay/NullDisplay';
import { Grid, Link, Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LayoutPage from "../../../templates/Layout/LayoutPage";
import DataGridTable from "../../../components/organisms/Table/DataGridTable";
import { SchedulesStyles } from "./SalarySchedules.styles";
import ReportButtons from "../../../components/organisms/BugReport/ReportButtons";
import SerbContractStatus from "./SerbContractStatus";
import { nonUnionDistricts } from "src/data/serbSnapshot";

function SalarySchedules() {
    const { user } = useAuth0();
    const metadata = user[`${process.env.REACT_APP_AUTH0_KEY}/district`];
    const navigate = useNavigate();

    const [districtOptions, setDistrictOptions] = useState([]);
    const [referenceDistrict, setReferenceDistrict] = useState(null);
    const [year, setYear] = useState(null);
    const [fileYear, setFileYear] = useState();

    const [salaryTableData, setSalaryTableData] = useState();
    const [currentContract, setCurrentContract] = useState();
    const [notes, setNotes] = useState()

    // Access control — same as Election History.
    useEffect(() => {
        if (metadata) {
            const userSessionType = metadata.hasOwnProperty('sessionType') ? metadata.sessionType : 'forbidden';

            switch (userSessionType) {
                case 'district-access':
                    const districtData = {
                        value: parseInt(metadata.districtIrn),
                        label: metadata.districtLabel
                    };
                    setReferenceDistrict(districtData);
                    break;
                case 'full-access':
                    window.localStorage.setItem('session-type', 'full-access');
                    break;
                case 'multiple-district-access':
                    window.localStorage.setItem('session-type', 'multiple-district-access');
                    break;
                case 'forbidden':
                    navigate("/AccessDenied", { replace: true });
                    break;
                default:
                    navigate("/AccessDenied", { replace: true });
                    break;
            }
        } else {
            navigate("/AccessDenied", { replace: true });
        }
    }, [navigate, user, metadata]);

    // Fetch all districts for dropdown — same as Election History.
    useEffect(() => {
        simpleFetch(`${process.env.REACT_APP_API_URL}/hvd_district?order=label`)
            .then(data => {
                setDistrictOptions(data);
                // If no district pre-selected yet, default to first.
                setReferenceDistrict(prev => prev || (data.length > 0 ? data[0] : null));
            });
    }, [user]);

    useEffect(() => {
        if (referenceDistrict?.value && year?.value) {
            let salaryTableEndpoint = `${process.env.REACT_APP_API_URL}/rpc/hvd_salary_schedule_table?_irn=${referenceDistrict.value}&_year=${year.value}`

            simpleFetch(salaryTableEndpoint)
                .then(data => {
                    const scheduleData = data?.[0];
                    if (!scheduleData) {
                        setSalaryTableData(undefined);
                        setCurrentContract(undefined);
                        setNotes(undefined);
                        setFileYear(undefined);
                        return;
                    }
                    const notes = (scheduleData?.notes)?.split("|") || [];
                    // Adding note if file year not match MCD current year.
                    // Non-Union districts never get a contract, so the SERB note does not apply.
                    const isNonUnion = !!nonUnionDistricts[String(referenceDistrict.value).padStart(6, "0")];
                    if(year.value === "MCD" && (scheduleData.file_year !== 2026) && !isNonUnion) {
                        notes.push("Salary data for the current year is not available in SERB.")
                    }
                    setNotes(notes);
                    // Validating if the district has steps for at least one academic level.
                    let steps = (scheduleData?.steps?.BA || scheduleData?.steps?.MA);
                    let contractUrl = scheduleData?.contrat_url;
                    setFileYear(scheduleData?.file_year)
                    let rows = []
                    let columns = []
                    if (steps) {
                        rows = (steps).map((el, index) => (
                            {
                                id: index,
                                // if the academic leve has steps filter based on the map index, if not return an empty array as value.
                                ba: scheduleData.steps.BA ? (scheduleData.steps?.BA).filter(el => el.step === index)[0] : '',
                                ma: scheduleData.steps.MA ? (scheduleData.steps?.MA).filter(el => el.step === index)[0] : '',
                            }
                        )).filter(el => el.id <= 39)
                    }

                    // Building index and salary columns BA/MA
                    ['ba', 'ma'].forEach( el => {
                       columns = [
                           ...columns,
                           ...[
                               {
                                   field: `index-${el}`,
                                   headerName: `Index ${el.toUpperCase()}`,
                                   minWidth: 150,
                                   flex: 1,
                                   sortable: false,
                                   align: 'center',
                                   cellClassName: (params) => {
                                    const isCalculatedIndex = params.row[el.toLowerCase()].is_calculated;

                                       return `index-column ${el}-column ${ isCalculatedIndex ? 'calculated_index': 'index' }`;
                                   },
                                   valueGetter: (params) => {
                                       let academicLevel = params.row[el.toLowerCase()];
                                       let fixedIndex = params.row[el.toLowerCase()].fixed_idx_value;
                                       return fixedIndex ? `${fixedIndex} (${academicLevel.idx_value})` : academicLevel.idx_value
                                   },
                                   renderCell: (params) => {
                                       let academicLevel = params.row[el.toLowerCase()];
                                       let fixedIndex = params.row[el.toLowerCase()].fixed_idx_value;
                                       return <span className={"index-value"}>{ fixedIndex ? `${fixedIndex} (${academicLevel.idx_value})` : academicLevel.idx_value}</span>
                                   }
                               },
                               {
                                   field: `salary-${el}`,
                                   headerName: `Salary ${el.toUpperCase()}`,
                                   minWidth: 150,
                                   sortable: false,
                                   flex: 1,
                                   cellClassName: (params) => {
                                    const isCalculatedIndex = params.row[el.toLowerCase()].is_calculated;

                                       return `salary-column ${el}-column ${ isCalculatedIndex ? 'calculated_index': 'index'}`
                                   },
                                   valueGetter: (params) => {
                                       let academicLevel = params.row[el.toLowerCase()];
                                       return !isNaN(academicLevel.value) ? currencyFormat(academicLevel.value) : NULL_DISPLAY_TEXT
                                   },
                                   renderCell: (params) => {
                                       let academicLevel = params.row[el.toLowerCase()];
                                       return  <span className={!isNaN(academicLevel.value) ? "salary-value" : "salary-value null-display"}>{!isNaN(academicLevel.value) ? currencyFormat(academicLevel.value) : NULL_DISPLAY_TEXT}</span>
                                   }
                               }

                           ]
                       ]
                    })

                    // Adding steps column
                    columns = [
                        {
                            field: 'id',
                            headerName: `Steps ${year.value === 'MCD' ? `${scheduleData?.file_year} (Most Current Data)` : year.label}`,
                            width: 160,
                            cellClassName: 'step-cell',
                            renderCell: (params) => `Step ${params.id}/${params.id + 1}` //`Step ${params.id -1 }/${params.id}`
                        },
                        ...columns
                    ]

                    setSalaryTableData({ columns: columns, rows: rows })
                    setCurrentContract(contractUrl)

                });
        }
    }, [referenceDistrict, year]);

    const topContentElement = (
        <Grid container spacing={2} alignItems="center" sx={{ padding: "16px" }}>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="referenceDistrict"
                    options={districtOptions}
                    noOptionsText={'Loading...'}
                    value={referenceDistrict}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth={true}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setReferenceDistrict(newValue);
                            setSalaryTableData(undefined);
                            window._paq?.push([
                                "trackEvent",
                                "Board Salary Schedules",
                                "Clicked: Reference District",
                                `Selected: ${newValue.label}`,
                            ]);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Reference district" />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="year"
                    options={yearList}
                    value={year}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth={true}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setYear(newValue);
                            setSalaryTableData(undefined);
                            window._paq?.push([
                                "trackEvent",
                                "Board Salary Schedules",
                                "Clicked: Year",
                                `Selected: ${newValue.label}`,
                            ]);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Year" />
                    )}
                />
            </Grid>
        </Grid>
    )

    const tableGroupingModel = (district) => [
        {
            groupId: `${district.value}-BA`,
            description: '',
            children: [{ field: 'index-ba' }, { field: 'salary-ba'}],
        },
        {
            groupId: `${district.value}-MA`,
            description: '',
            children: [{ field: 'index-ma' }, { field: 'salary-ma'}],
        },
    ];

    return (
        <LayoutPage
            pageTitle={<div>Salary Schedules <span style={{color: "#FFDF00"}}>(Beta)</span></div>}
            documentTitle="Salary Schedules (Beta)"
            description="Salary Schedules"
            type="dashboard"
            customClass="salary_schedules"
            topContent={topContentElement}
        >
            <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                { salaryTableData &&
                    <Grid item xs={12}>
                        <Box sx={SchedulesStyles}>
                            <div className={"salary-table__title"}>{referenceDistrict.label} - {year.value === 'MCD' ? `${fileYear ?? "Not Found"} (Most Current Data)` : year.label}</div>
                            <DataGridTable
                                columns={salaryTableData.columns}
                                rows={salaryTableData.rows}
                                trim={false}
                                groupingModel={tableGroupingModel(referenceDistrict)}
                                customStyles={{
                                    '@media print': {
                                        '& .MuiDataGrid-columnHeadersInner div[role="row"]': {
                                            width: "100% !important",
                                        },
                                        '& .MuiDataGrid-columnHeadersInner div[role="row"]:first-of-type .MuiDataGrid-columnHeader:not(:first-of-type)': {
                                            minWidth: "inherit !important",
                                            flex: "1",
                                        },
                                        '& .MuiDataGrid-columnHeadersInner div[role="row"]:not(:first-of-type) .MuiDataGrid-columnHeader:not(:first-of-type)': {
                                            minWidth: "inherit !important",
                                            flex: "1",
                                        },
                                        '& .MuiDataGrid-columnHeadersInner div[role="row"]:not(:first-of-type) .MuiDataGrid-columnHeaderTitleContainerContent': {
                                            width: "100% !important",
                                        },
                                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                                            width: "100% !important",
                                        },
                                        '& .MuiDataGrid-row': {
                                            width: "100% !important",
                                        },
                                        '& .MuiDataGrid-row .index-column': {
                                            justifyContent: "flex-start !important",
                                        },
                                        '& .MuiDataGrid-row .MuiDataGrid-cell:not(:first-of-type)': {
                                            minWidth: "inherit !important",
                                            flex: "1",
                                        },
                                    },
                                }}
                            />
                            <div className="salary-table__notes">
                                {currentContract &&
                                    <div className={"note-item"}>
                                        Please note that the more current contract year is available in:
                                        <div>
                                            <Link href={currentContract} color="primary" target={"_blank"} rel="noreferrer">
                                                {currentContract}
                                            </Link>
                                        </div>
                                    </div>
                                }
                                <div className={"note-item"}>
                                    <span></span>
                                    Denotes non-contracted steps. The final step has been repeated to calculate Total Accumulated Lifetime.
                                </div>
                                {
                                    notes?.length > 0 &&
                                    <div className={"editors-notes"}>
                                        <div className={"label"}>Editor's notes:</div>
                                        <ul>
                                            {
                                                notes?.map( (note, i) => // Footnote for Vanlue Local SD, Hancock (047472)
                                                    <li key={i} className={"note-item"}>
                                                        { note.replaceAll('"', '') }
                                                    </li>)
                                            }
                                        </ul>
                                    </div>

                                }
                            </div>
                        </Box>
                        { year?.value === "MCD" && <SerbContractStatus /> }
                    </Grid>
                }
            </Grid>
            <ReportButtons
                boardType={"Board Salary Schedules"}
                collectedData={{
                    referenceDistrict: referenceDistrict,
                    year: { value: year?.value, label: year?.value },
                }}
            />
        </LayoutPage>
    );
}

export default SalarySchedules;